import {userFind} from '../../../store/user';
import {FindUsersRequest, UserDTO, ModelDTO} from 'hemwb-api';
import {fullName} from '../../../store/user/utils';
import {AutocompleteControl} from './AutocompleteControl';
import type {AutocompleteProps} from '@mui/material/Autocomplete';
import React, {useCallback} from 'react';
import {AutocompleteItem} from './AutocompleteItem';
import {tid} from '../../../testUtils';
import {useDispatch} from 'react-redux';
import {UserSelectHandler} from './utils';

type AutocompleteUserControlProps = {
  name: string;
  label: React.ReactNode;
  TextFieldProps?: any;
  AutocompleteProps?: Partial<AutocompleteProps<any, true, true, false>>;
  disableNoOptionsForEmptySearch?: boolean;
  subModelAccess?: boolean;
  wait?: number;
  search?: (inputValue: string) => Promise<any[]>;
  loading?: boolean;
  plural?: boolean;
  required?: boolean;
  disableDeletingTagsByKeyboard?: boolean;
  disabled?: boolean;
  userFindOptions?: Partial<FindUsersRequest>;
  model?: ModelDTO;
  theoremModel?: boolean;
  fileUuid?: string;
  handleSecurityAPI?: () => void;
  restrictAccessOfExternalUser?: (name: string) => void;
  onSelect?: UserSelectHandler; // Generic user selection handler
};

const userFindOptionsDefault = {};

const AutocompleteUserControl: React.FC<AutocompleteUserControlProps> = (props) => {
  const {
    userFindOptions = userFindOptionsDefault,
    model,
    theoremModel,
    fileUuid,
    handleSecurityAPI,
    restrictAccessOfExternalUser,
    onSelect,
    ...restProps
  } = props;
  const dispatch = useDispatch();
  const findUsersCallback = useCallback(
    (value: string) => {
      return userFind(dispatch, value.replace(/[<>]/g, ''), userFindOptions);
    },
    [dispatch, userFindOptions],
  );
  const name = props.name || '';

  const renderUserItem = (user: UserDTO, name: string, modelUuid: string, theoremModel: boolean) => (
    <AutocompleteItem
      handleSecurityAPI={handleSecurityAPI}
      restrictAccessOfExternalUser={restrictAccessOfExternalUser}
      onSelectGeneric={onSelect}
      user={user}
      name={name}
      theoremModel={theoremModel}
      modelUuid={modelUuid}
      fileUuid={fileUuid}
      isid={user.id}
      userType={user.userType}
      externalUserType={user.externalUserType}
      informationClassification={model?.metadata.information_classification}
      primary={fullName(user)}
      secondary={user.email || ''}
      {...tid('input-option', name, user.id)}
    />
  );

  return (
    <AutocompleteControl
      wait={1000}
      search={findUsersCallback}
      disableNoOptionsForEmptySearch
      disableDeletingTagsByKeyboard
      {...restProps}
      AutocompleteProps={{
        isOptionEqualToValue: (option: UserDTO, value: UserDTO) => {
          return option.id === value.id;
        },
        getOptionLabel: (option: UserDTO) => {
          const optionEmail = ` <${option.email}>`;
          return `${fullName(option)}${option.email ? optionEmail : ''}`;
        },
        renderOption: (props: object, option: UserDTO) =>
          renderUserItem(option, name || '', model?.uuid || '', theoremModel || false),
        //disableOpenOnFocus: true,
        ...props.AutocompleteProps,
      }}
    />
  );
};

export default AutocompleteUserControl;
